import React, { useMemo } from 'react';
import { Confirmation, Field, Form, HelpTooltip, Tooltip } from '~/components';
import { useConfirmation } from '~/contexts';
import { useFeatures } from '~/hooks';
import SettingsFormSection from '../../SettingsFormSection';
import LabelTooltip from './LabelTooltip';

export default function Settings({ project, formik }) {
  const features = useFeatures();
  const confirmation = useConfirmation();

  const { client, isBillable, useDependencies, defaultExpenseItemIsBillable } = formik.values;
  const locked = project.hasInvoices || project.hasCreditNotes;

  const handleUseBudgetChange = ({ target: { checked } }) => {
    const values = { ...formik.values, useBudget: checked };

    // If it's a fixed fee project and the overall budget is turned off, turn off the monthly budget.
    // This is because the overall budget hours are used to calculate the monthly revenue.
    if (values.billingTypeId === 'fixed' && !values.useBudget) values.useMonthlyBudget = false;

    formik.setValues(values);
  };

  const handleUseDependenciesChange = async ({ target: { checked } }) => {
    if (!checked && useDependencies) {
      const confirmed = await confirmation.prompt((resolve) => (
        <Confirmation resolve={resolve}>
          Disabling task dependencies will hide all existing blocking relationships on this project. You can enable this
          setting again later to restore them. Are you sure you want to continue?
        </Confirmation>
      ));
      if (!confirmed) return;
    }
    formik.setFieldValue('useDependencies', checked);
  };

  const budgetDisabled = useMemo(
    () =>
      ['fixed', 'fixed_recurring'].includes(formik.values.billingTypeId) &&
      ['automated_hours', 'automated_tm_basis', 'automated_services_cost'].includes(
        formik.values.revenueRecognitionMethod,
      ) &&
      formik.values.useBudget,
    [formik.values.revenueRecognitionMethod, formik.values.useBudget, formik.values.billingTypeId],
  );

  const monthlyBudgetDisabled = useMemo(
    () => formik.values.billingTypeId === 'fixed' && !formik.values.useBudget,
    [formik.values.billingTypeId, formik.values.useBudget],
  );

  return (
    <SettingsFormSection title="Settings">
      <Form.Control>
        <LabelTooltip>
          <Field.Checkbox label="Use roles" name="useRoles" disabled={locked} />
          <HelpTooltip
            message="Team members can be assigned to one or more roles and each role will have its own bill rate."
            style={{ marginLeft: '0.5rem' }}
          />
        </LabelTooltip>
      </Form.Control>

      {features.rateCards && (
        <Form.Control>
          <LabelTooltip>
            <Field.Checkbox label="Use rate cards" name="useRateCards" />
            <HelpTooltip
              message="Change bill and cost rates over time with dated rate cards."
              style={{ marginLeft: '0.5rem' }}
            />
          </LabelTooltip>
        </Form.Control>
      )}

      <Form.Control>
        <LabelTooltip>
          <Field.Checkbox label="Use task dependencies" name="useDependencies" onChange={handleUseDependenciesChange} />
          <HelpTooltip
            message="Allow tasks on this project to block or be blocked by other tasks."
            style={{ marginLeft: '0.5rem' }}
          />
        </LabelTooltip>
      </Form.Control>

      {useDependencies && (
        <Form.Control>
          <LabelTooltip>
            <Field.Checkbox
              label="Automatically complete milestones when all blocking tasks are complete"
              name="autoCompleteMilestones"
            />
            <HelpTooltip
              message="When all of a milestone's blocking tasks are complete, mark the milestone complete automatically."
              style={{ marginLeft: '0.5rem' }}
            />
          </LabelTooltip>
        </Form.Control>
      )}

      <Form.Control>
        <LabelTooltip>
          <Tooltip
            message={
              budgetDisabled && !formik.errors.useBudget
                ? 'An overall budget is required for the selected Revenue Recognition Method.'
                : undefined
            }>
            <Field.Checkbox
              name="useBudget"
              label="Track overall budget"
              onChange={handleUseBudgetChange}
              disabled={budgetDisabled}
            />
          </Tooltip>
        </LabelTooltip>
        <Field.Checkbox name="useMonthlyBudget" label="Track monthly budget" disabled={monthlyBudgetDisabled} />
      </Form.Control>

      {features.projectHealth && (
        <Form.Control>
          <Field.Checkbox name="useHealthReports" label="Track project health" />
        </Form.Control>
      )}

      <Form.Control>
        <LabelTooltip>
          <Field.Checkbox name="lockTimeAndExpenses" label="Lock time and expenses" />
          <HelpTooltip
            message="Prevent time entries and expense items on this project from being created or updated."
            style={{ marginLeft: '0.5rem' }}
          />
        </LabelTooltip>
      </Form.Control>

      <Form.Control>
        <LabelTooltip>
          <Field.Checkbox
            name="defaultExpenseItemIsBillable"
            label="Expense items are billable by default"
            checked={isBillable ? defaultExpenseItemIsBillable : false}
            disabled={!isBillable}
          />
        </LabelTooltip>
      </Form.Control>

      {client.isInternal && (
        <Form.Control>
          <LabelTooltip>
            <Field.Checkbox name="isProductive" label="This internal project counts toward productive utilization" />
            <HelpTooltip
              message="All time tracked to this project will count towards a member's productive utilization."
              style={{ marginLeft: '0.5rem' }}
            />
          </LabelTooltip>
        </Form.Control>
      )}

      <Form.Control>
        <Field.Checkbox label="Automatically assign new workspace members to this project" name="autoAssignMembers" />
      </Form.Control>

      <Form.Control>
        <Field.Checkbox
          label="Notify members when they are added to this project team"
          name="assignmentNotifications"
        />
      </Form.Control>
    </SettingsFormSection>
  );
}
